/** @format */

import type { AnimationItem } from "lottie-web";
import lottie from "lottie-web/build/player/lottie_light.min.js";
import React, { type FC, memo, useEffect, useRef } from "react";
import styled from "styled-components";
import waveBeige from "../../../assets/lottieFiles/senf-wave-beige.json";
import waveSenf from "../../../assets/lottieFiles/senf-wave-senf.json";
import type { WaveProps } from "./Wave.types";

interface BlockProps {
  id: string;
  color?: string;
  height?: string;
}

const Wrapper = styled.div<WaveProps>`
  width: 100%;
  height: ${({ height }) => height || "100%"};
  position: absolute;
  overflow: hidden;
  left: ${({ left }) => left || undefined};
  top: ${({ top }) => top || undefined};
  bottom: ${({ bottom }) => bottom || undefined};
  position: ${({ position }) => position || "absolute"};
  z-index: 0;
  pointer-events: none;
  transition: 2s;
`;

const Block = styled.div<BlockProps>`
  display: block;
  position: relative;
  width: 150%;
  height: ${({ height }) => height};
  margin-top: -10px;
  opacity: 1;
`;

const Wave: FC<WaveProps> = ({
  position,
  top,
  left,
  bottom,
  height = "1570px",
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    let animationInstance: AnimationItem | null = null;

    let isComponentMounted = true;

    const initializeAnimation = async () => {
      try {
        if (!isComponentMounted || !containerRef.current) return;

        animationInstance = lottie.loadAnimation({
          container: containerRef.current,
          renderer: "svg",
          loop: true,
          autoplay: true,
          animationData: waveSenf,
        });
      } catch (error) {
        if (isComponentMounted) {
          if (typeof window !== "undefined" && window.Sentry) {
            window.Sentry.captureException(error);
            console.error("Failed to load Wave animation:", error, {
              component: "Wave",
              message: "Failed to load Wave animation",
            });
          } else {
            console.error("Failed to load Wave animation:", error);
          }
        }
      }
    };

    // Start the animation initialization
    initializeAnimation();

    // Cleanup function that runs when component unmounts
    return () => {
      isComponentMounted = false;
      if (animationInstance) {
        animationInstance.destroy();
      }
    };
  }, []);

  return (
    <Wrapper
      position={position}
      top={top}
      height={height}
      bottom={bottom}
      left={left}
    >
      <div ref={containerRef} />
      <Block
        id="wave"
        height={height}
        className="bg-surface-highlight-default"
      />
    </Wrapper>
  );
};

export default memo(Wave);
